/**
 * SearchField: the house search input. A leading search icon, a
 * type=search input with the house border and height, and a clear
 * button that appears once there is text and returns focus to the
 * input when clicked.
 *
 * Two sizes: `default` is the house input and tab height (2.75rem);
 * `compact` is the Workspace band's field (2.34375rem).
 *
 * The value is the caller's: `value` and `onChange(next)` make it a
 * controlled input, and nothing here reaches the URL or any store.
 * `autoComplete` is off, so the browser offers no earlier entries, and
 * the browser's own cancel glyph is hidden, so there is one clear
 * control, not two.
 *
 * Escape with text empties the field, keeps focus and stops the event
 * before a surrounding dialog's document listener sees it; Escape on an
 * empty field bubbles as usual. The caller's `onKeyDown` runs first and
 * can call `preventDefault()` to keep the text.
 *
 * Pressing the clear button never takes focus from the input: its
 * mousedown is prevented, so a caller's blur handler does not see a
 * pointer clear. The keyboard path (Tab to the button, then Enter or
 * Space) does blur the input first, and the clear returns focus to it.
 *
 * A disabled field shows no clear button, and it takes the house
 * disabled look: the not-allowed cursor at half opacity.
 *
 * A placeholder or unfocused value too long for its box ends in an
 * ellipsis. Every placeholder in the app is chosen to show whole at 320
 * CSS px, at 200% text at 1280, and at 390; the ellipsis guards a
 * string that was not measured, such as a translation.
 *
 * The accessible name comes from the caller: `aria-label`, or `id` with
 * an external label. SearchField never wraps itself in a label, because
 * its clear button is itself labelable and must not sit inside one. The
 * rest of the native input surface (`id`, `autoFocus`, `aria-*`,
 * `onFocus`, `onKeyDown`, `ref`) passes through to the input.
 */

import { useCallback, useRef, type ComponentPropsWithRef, type KeyboardEvent, type ReactNode } from 'react';
import { Search, X } from 'lucide-react';

type NativeInputProps = Omit<ComponentPropsWithRef<'input'>, 'type' | 'value' | 'onChange' | 'className' | 'size'>;

export interface SearchFieldProps extends NativeInputProps {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  /** The clear button's accessible name. */
  clearLabel?: string;
  /** default: 2.75rem, the house input and tab height. compact: 2.34375rem, the Workspace band's field. */
  size?: 'default' | 'compact';
  /** The input's data-testid; the wrapper, icon and clear button take `${testId}-field`, `${testId}-icon` and `${testId}-clear`. */
  testId?: string;
  /** Classes for the wrapper: its width and placement in the caller's layout. */
  className?: string;
}

const INPUT_CLASSES = {
  base:
    'w-full rounded-base border border-sage-300 bg-paper pl-9 text-ink placeholder:text-ink-soft ' +
    'transition-colors duration-[var(--dur-base)] ease-quiet motion-reduce:transition-none ' +
    'hover:border-sage-400 focus:border-sage-400 focus-visible:outline-none focus-visible:ring-2 ' +
    'focus-visible:ring-sage-400 [&::-webkit-search-cancel-button]:appearance-none text-ellipsis ' +
    'disabled:cursor-not-allowed disabled:opacity-50',
  default: 'min-h-11 pr-11 text-sm',
  compact: 'min-h-[2.34375rem] pr-9 text-[0.8125rem]',
} as const;

const CLEAR_CLASSES = {
  base:
    'absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center rounded-base ' +
    'text-sage-500 transition-colors duration-[var(--dur-base)] ease-quiet motion-reduce:transition-none ' +
    'hover:bg-sage-800/[6%] hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 ' +
    'focus-visible:ring-sage-400',
  default: 'h-9 w-9',
  // The 28px button keeps the 36px touch floor through the same invisible
  // hit extension the Button and FilterPill primitives carry.
  compact: "h-7 w-7 before:absolute before:-inset-1 before:content-['']",
} as const;

export function SearchField({
  ref,
  value,
  onChange,
  placeholder,
  clearLabel = 'Clear search',
  size = 'default',
  testId,
  className,
  onKeyDown,
  ...rest
}: SearchFieldProps): ReactNode {
  // The internal ref focuses the input after a clear; the caller's ref,
  // object or callback, is kept in step with it.
  const inputRef = useRef<HTMLInputElement | null>(null);
  const setInputRef = useCallback(
    (el: HTMLInputElement | null) => {
      inputRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) ref.current = el;
    },
    [ref],
  );

  function clear(): void {
    onChange('');
    inputRef.current?.focus();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (e.key === 'Escape' && value !== '') {
      e.preventDefault();
      e.stopPropagation();
      onChange('');
    }
  }

  return (
    <div
      data-testid={testId ? `${testId}-field` : undefined}
      className={['relative', className].filter(Boolean).join(' ')}
    >
      <Search
        size={16}
        aria-hidden="true"
        data-testid={testId ? `${testId}-icon` : undefined}
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sage-400"
      />
      <input
        {...rest}
        ref={setInputRef}
        type="search"
        autoComplete="off"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        data-testid={testId}
        className={`${INPUT_CLASSES.base} ${INPUT_CLASSES[size]}`}
      />
      {value !== '' && !rest.disabled && (
        <button
          type="button"
          aria-label={clearLabel}
          data-testid={testId ? `${testId}-clear` : undefined}
          onMouseDown={(e) => e.preventDefault()}
          onClick={clear}
          className={`${CLEAR_CLASSES.base} ${CLEAR_CLASSES[size]}`}
        >
          <X size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
